export type TranscriptWord = {
  start: number;
  end: number;
  text: string;
};

export type TranscriptSegment = {
  start: number;
  end: number;
  text: string;
  words?: TranscriptWord[];
};

export type CourseTranscript = {
  version: number;
  language: string;
  source: string;
  duration: number;
  segments: TranscriptSegment[];
};

const COURSE_TRANSCRIPT_CDN_PREFIX =
  "https://cdn.autoclipper.live/courses/viralize-em-4-semanas/transcripts/";

export function resolveTranscriptRequestUrl(transcriptUrl: string): string {
  if (!transcriptUrl.startsWith(COURSE_TRANSCRIPT_CDN_PREFIX)) {
    return transcriptUrl;
  }

  return `/course-transcripts/${transcriptUrl.slice(
    COURSE_TRANSCRIPT_CDN_PREFIX.length
  )}`;
}

function isTranscriptWord(value: unknown): value is TranscriptWord {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<TranscriptWord>;
  return (
    typeof candidate.start === "number" &&
    typeof candidate.end === "number" &&
    typeof candidate.text === "string"
  );
}

function isTranscriptSegment(value: unknown): value is TranscriptSegment {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<TranscriptSegment>;
  return (
    typeof candidate.start === "number" &&
    typeof candidate.end === "number" &&
    typeof candidate.text === "string" &&
    (candidate.words === undefined ||
      (Array.isArray(candidate.words) &&
        candidate.words.every(isTranscriptWord)))
  );
}

export function isCourseTranscript(value: unknown): value is CourseTranscript {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<CourseTranscript>;
  return (
    Array.isArray(candidate.segments) &&
    candidate.segments.every(isTranscriptSegment)
  );
}

export async function fetchCourseTranscript(
  transcriptUrl: string,
  signal: AbortSignal
): Promise<CourseTranscript> {
  const response = await fetch(resolveTranscriptRequestUrl(transcriptUrl), {
    signal,
  });

  if (!response.ok) throw new Error("transcript_request_failed");
  const payload: unknown = await response.json();
  if (!isCourseTranscript(payload)) throw new Error("invalid_transcript");
  return payload;
}

export function getTimedTranscriptWords(
  segment: TranscriptSegment
): TranscriptWord[] {
  if (segment.words?.length) return segment.words;

  const tokens = segment.text.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];

  const segmentDuration = Math.max(0, segment.end - segment.start);
  const wordDuration = segmentDuration / tokens.length;

  return tokens.map((text, index) => ({
    start: segment.start + index * wordDuration,
    end:
      index === tokens.length - 1
        ? segment.end
        : segment.start + (index + 1) * wordDuration,
    text,
  }));
}

export function findActiveTranscriptSegmentIndex(
  segments: TranscriptSegment[],
  currentTime: number
): number {
  for (let index = segments.length - 1; index >= 0; index -= 1) {
    const segment = segments[index];
    if (currentTime >= segment.start && currentTime < segment.end) {
      return index;
    }
  }

  if (
    segments.length > 0 &&
    currentTime === segments[segments.length - 1].end
  ) {
    return segments.length - 1;
  }

  return -1;
}

export function findActiveTranscriptWordIndex(
  words: TranscriptWord[],
  currentTime: number
): number {
  if (words.length === 0) return -1;

  const activeIndex = words.findIndex(
    (word) => currentTime >= word.start && currentTime < word.end
  );
  if (activeIndex >= 0) return activeIndex;
  if (currentTime < words[0].start) return 0;
  return words.length - 1;
}

export function getTranscriptCaptionWindow(
  segment: TranscriptSegment,
  currentTime: number,
  maximumWords = 9
): { words: TranscriptWord[]; activeWordIndex: number } {
  const words = getTimedTranscriptWords(segment);
  const activeWordIndex = findActiveTranscriptWordIndex(words, currentTime);
  if (activeWordIndex < 0) return { words: [], activeWordIndex: -1 };

  const safeMaximumWords = Math.max(1, maximumWords);
  const windowStart =
    Math.floor(activeWordIndex / safeMaximumWords) * safeMaximumWords;

  return {
    words: words.slice(windowStart, windowStart + safeMaximumWords),
    activeWordIndex: activeWordIndex - windowStart,
  };
}
